import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Phone, ChevronDown, ArrowUpRight, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import SEO from "../components/SEO";
import PlaceAutocompleteInput from "../components/PlaceAutocompleteInput";
import { getRoute, ROUTES } from "../data/routes";
import { API_BASE, formatApiError } from "../config/api";

const API = API_BASE;
const emptyLocation = (address = "") => ({ address, lat: null, lng: null, place_id: null });

const CAR_IMG = {
  id4: "https://customer-assets-4nw71qhi.emergentagent.net/job_brothers-taxi-web/artifacts/qljglrgo_brothers%20taxi%20id4%20tirana%20airport.png",
  byd: "https://customer-assets-4nw71qhi.emergentagent.net/job_brothers-taxi-web/artifacts/q718vh2m_byd%20sarande%20brotherstaxi.png",
  gla: "https://customer-assets-4nw71qhi.emergentagent.net/job_brothers-taxi-web/artifacts/9ayxnyuq_brotherstaxi%20gla%20albania.png",
};

const CTA_IMG = "https://customer-assets-4nw71qhi.emergentagent.net/job_brothers-taxi-web/artifacts/gmifdzt1_brothers%20taxi%20id4%20tirana%20airport.png";
const DEFAULT_HERO = "https://customer-assets-4nw71qhi.emergentagent.net/job_brothers-taxi-web/artifacts/qk1lsm67_brothers%20taxi%20tirana.png";

const fleet = [
  {
    name: "Volkswagen ID.4",
    cat: "SUV Elektrik",
    desc: "SUV elektrik i bollshëm dhe i qetë, i përshtatshëm për transferta dhe udhëtime të gjata.",
    caps: ["Deri në 4 pasagjerë", "3–4 valixhe"],
    img: CAR_IMG.id4,
  },
  {
    name: "BYD Song Plus",
    cat: "SUV Premium Elektrik",
    desc: "Hapësirë dhe komoditet për familje, turistë dhe klientë biznesi.",
    caps: ["Deri në 4 pasagjerë", "Deri në 4 valixhe"],
    img: CAR_IMG.byd,
  },
  {
    name: "Mercedes-Benz GLA",
    cat: "SUV Premium",
    desc: "Automjet modern dhe komod për transferta private dhe udhëtime biznesi.",
    caps: ["Deri në 4 pasagjerë", "Deri në 3 valixhe"],
    img: CAR_IMG.gla,
  },
];

const includedItems = [
  "Marrje nga adresa e konfirmuar",
  "Transport privat",
  "Shofer profesional",
  "Hapësirë për bagazhe",
  "Çmim i konfirmuar paraprakisht",
  "Ndihmë për ndryshime në rezervim",
  "Mundësi rezervimi për kthim",
];

const formatLek = (v) => `${Math.round(v).toLocaleString("sq-AL")} L`;

const RouteDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const key = slug ? slug.replace(/^taxi-lushnje-/, "") : "";
  const staticRoute = getRoute(key);

  const [route, setRoute] = useState(staticRoute);
  const [loading, setLoading] = useState(!staticRoute);

  useEffect(() => {
    // If not in static data, fetch from backend by slug
    if (!staticRoute && key) {
      setLoading(true);
      axios
        .get(`${API}/pricing/by-slug/${encodeURIComponent(key)}`)
        .then((r) => {
          const p = r.data;
          setRoute({
            origin: "Lushnje",
            destination: p.route_to,
            slug: `taxi-lushnje-${key}`,
            price_all: p.price_all,
            price_eur: Math.round(p.price_all / 100),
            distance_km: p.distance_km || null,
            duration_text: p.duration_min ? `Rreth ${p.duration_min} minuta` : "Sipas trafikut",
          });
        })
        .catch(() => setRoute(null))
        .finally(() => setLoading(false));
    }
  }, [key, staticRoute]);

  const [form, setForm] = useState({
    pickup: emptyLocation("Lushnje"),
    dropoff: emptyLocation(""),
    date: "",
    time: "",
    passengers: 1,
    luggage: 0,
    name: "",
    phone: "",
    email: "",
    notes: "",
  });
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (route) {
      setForm((f) => ({
        ...f,
        pickup: emptyLocation(route.origin || "Lushnje"),
        dropoff: emptyLocation(route.destination || ""),
      }));
    }
  }, [route]);

  if (loading) {
    return (
      <section className="pt-40 pb-32 text-center max-w-[1180px] mx-auto px-6">
        <Loader2 className="h-6 w-6 animate-spin mx-auto text-zinc-500" />
      </section>
    );
  }

  if (!route) {
    return (
      <section className="pt-40 pb-32 text-center max-w-[1180px] mx-auto px-6">
        <p className="text-zinc-600">Rruga nuk u gjet.</p>
        <button onClick={() => navigate("/pricing")} className="bt-btn-primary mt-6 mx-auto w-fit">
          Shiko çmimet
        </button>
      </section>
    );
  }

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    if (!form.phone || !isValidPhoneNumber(form.phone)) {
      setStatus("error");
      setErrorMsg("Numri i telefonit nuk është i vlefshëm.");
      return;
    }
    if (!form.pickup.address?.trim() || form.pickup.address.trim().length < 3) {
      setStatus("error");
      setErrorMsg("Zgjidhni ose shkruani adresën e marrjes.");
      return;
    }
    if (!form.dropoff.address?.trim() || form.dropoff.address.trim().length < 3) {
      setStatus("error");
      setErrorMsg("Zgjidhni ose shkruani destinacionin.");
      return;
    }

    setStatus("submitting");
    try {
      const routeNote = `Rruga: ${route.origin} → ${route.destination}` +
        (route.price_all ? ` | Çmim listë: ${Math.round(route.price_all)} L` : "") +
        (route.slug ? ` | ${route.slug}` : "");
      const userNotes = form.notes?.trim() || "";
      const notes = userNotes ? `${routeNote}\n${userNotes}` : routeNote;

      const payload = {
        full_name: form.name.trim(),
        phone: form.phone,
        email: form.email?.trim() || undefined,
        pickup_location: form.pickup.address.trim(),
        dropoff_location: form.dropoff.address.trim(),
        pickup_lat: form.pickup.lat ?? undefined,
        pickup_lng: form.pickup.lng ?? undefined,
        dropoff_lat: form.dropoff.lat ?? undefined,
        dropoff_lng: form.dropoff.lng ?? undefined,
        pickup_date: form.date,
        pickup_time: form.time,
        passengers: Number(form.passengers) || 1,
        luggage: Number(form.luggage) || 0,
        service_type: "long_distance",
        vehicle_type: "sedan",
        notes,
        // Fixed list price from website /pricing for this destination
        estimated_price: route.price_all != null ? Number(route.price_all) : undefined,
        price_all: route.price_all != null ? Number(route.price_all) : undefined,
      };
      if (!payload.email) delete payload.email;
      if (payload.pickup_lat == null) delete payload.pickup_lat;
      if (payload.pickup_lng == null) delete payload.pickup_lng;
      if (payload.dropoff_lat == null) delete payload.dropoff_lat;
      if (payload.dropoff_lng == null) delete payload.dropoff_lng;
      if (payload.estimated_price == null) delete payload.estimated_price;
      if (payload.price_all == null) delete payload.price_all;

      // Proxied by nginx to dispatcher backend (8001)
      await axios.post(`${API}/bookings`, payload, {
        headers: { "Content-Type": "application/json" },
        timeout: 30000,
      });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        formatApiError(err.response?.data?.detail, null) ||
          (err.message === "Network Error"
            ? "Lidhja me serverin dështoi. Provoni përsëri."
            : "Rezervimi dështoi. Provoni përsëri ose telefononi.")
      );
    }
  };

  const related = (route.related || Object.keys(ROUTES).filter((k) => k !== key).slice(0, 6))
    .map((k) => ROUTES[k])
    .filter(Boolean);

  // JSON-LD structured data
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "TaxiService",
    name: `Taxi ${route.origin} – ${route.destination}`,
    provider: {
      "@type": "LocalBusiness",
      name: "Brothers Taxi",
      telephone: "+355682222286",
      address: { "@type": "PostalAddress", addressLocality: "Lushnjë", addressCountry: "AL" },
      url: "https://brotherstaxi.al",
    },
    areaServed: [route.origin, route.destination],
    offers: {
      "@type": "Offer",
      price: route.price_all,
      priceCurrency: "ALL",
      description: `Transfer privat një drejtim, ${route.origin} → ${route.destination}`,
    },
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Kreu", item: "https://brotherstaxi.al/" },
      { "@type": "ListItem", position: 2, name: "Çmimet", item: "https://brotherstaxi.al/pricing" },
      { "@type": "ListItem", position: 3, name: `${route.origin} – ${route.destination}`, item: `https://brotherstaxi.al/${route.slug}` },
    ],
  };

  return (
    <>
      <SEO
        title={`Taxi ${route.origin} – ${route.destination} | Çmimi dhe Rezervimi | Brothers Taxi`}
        description={`Rezervoni taxi nga ${route.origin} në ${route.destination} me Brothers Taxi. Shikoni çmimin (${formatLek(route.price_all)}), distancën (${route.distance_km} km), kohëzgjatjen dhe dërgoni kërkesën online.`}
        keywords={`taxi ${route.origin} ${route.destination}, taxi Lushnje ${route.destination}, çmim taxi ${route.origin} ${route.destination}, transfer privat ${route.destination}, rezervo taxi ${route.destination}, Brothers Taxi ${route.destination}`}
        path={`/${route.slug}`}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />

      {/* ============ HERO ============ */}
      <section className="relative min-h-[720px] md:min-h-[780px] flex items-end pb-24 md:pb-32 overflow-hidden">
        <img
          src={route.hero_image || DEFAULT_HERO}
          alt={`Taxi nga ${route.origin} në ${route.destination} — Brothers Taxi`}
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* asymmetric overlay - stronger left */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(21,21,21,0.75) 0%, rgba(21,21,21,0.5) 40%, rgba(21,21,21,0.15) 100%)",
          }}
        />

        <div className="relative w-full max-w-[1180px] mx-auto px-6 md:px-10 pt-32">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="text-white/80 text-xs md:text-sm mb-6">
            <ol className="flex flex-wrap items-center gap-1.5">
              <li><Link to="/" className="hover:text-white">Kreu</Link></li>
              <li className="opacity-60">/</li>
              <li><Link to="/pricing" className="hover:text-white">Destinacionet</Link></li>
              <li className="opacity-60">/</li>
              <li className="text-white font-medium">{route.origin} – {route.destination}</li>
            </ol>
          </nav>

          <div className="max-w-2xl">
            <div className="inline-block text-[11px] uppercase tracking-[0.22em] font-semibold text-[#FFD400] mb-5">
              Transfer privat nga {route.origin}
            </div>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold text-white tracking-tight leading-[1.05]">
              Taxi nga <span className="font-serif italic font-normal">{route.origin}</span> në {route.destination}
            </h1>
            <p className="mt-6 text-white/85 text-base md:text-lg leading-relaxed max-w-xl">
              {route.intro || `Udhëtoni drejt ${route.destination}it me automjet privat, shofer profesional dhe çmim të konfirmuar përpara nisjes.`}
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#booking" className="inline-flex items-center gap-2 bg-[#FFD400] text-[#151515] font-semibold rounded-full px-6 py-3.5 text-sm hover:bg-white transition-colors">
                Rezervo Taxi <ArrowRight className="h-4 w-4" />
              </a>
              <a href="tel:+355682222286" className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/25 text-white font-semibold rounded-full px-6 py-3.5 text-sm hover:bg-white hover:text-[#151515] transition-colors">
                <Phone className="h-4 w-4" /> Telefono tani
              </a>
            </div>

            <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-white/85 text-sm">
              <li className="inline-flex items-center gap-2"><span className="h-1 w-1 rounded-full bg-[#FFD400]" /> Automjet privat</li>
              <li className="inline-flex items-center gap-2"><span className="h-1 w-1 rounded-full bg-[#FFD400]" /> Marrje nga adresa</li>
              <li className="inline-flex items-center gap-2"><span className="h-1 w-1 rounded-full bg-[#FFD400]" /> Çmim i konfirmuar paraprakisht</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ============ ROUTE INFO CARD (overlaps hero) ============ */}
      <section className="relative -mt-16 md:-mt-20 z-10">
        <div className="max-w-[1180px] mx-auto px-6 md:px-10">
          <div className="bg-white rounded-3xl border border-[#E3E1DA] shadow-[0_20px_60px_-20px_rgba(21,21,21,0.18)] p-6 md:p-8">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-6 md:gap-4 items-center">
              <RouteMeta label="Nisja" value={route.origin} />
              <RouteMeta label="Destinacioni" value={route.destination} />
              <RouteMeta label="Distanca" value={`${route.distance_km} km`} />
              <RouteMeta label="Kohëzgjatja" value={route.duration_text} />
              <div className="col-span-2 md:col-span-1">
                <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#6D6D69] mb-1">Çmimi</div>
                <div className="font-display text-xl md:text-2xl font-semibold text-[#151515] tracking-tight leading-none">
                  {formatLek(route.price_all)}
                </div>
                <div className="text-[#6D6D69] text-xs mt-1">për automjet · një drejtim</div>
              </div>
              <div className="col-span-2 md:col-span-1">
                <a href="#booking" className="w-full inline-flex items-center justify-center gap-2 bg-[#151515] text-white font-semibold rounded-full px-5 py-3.5 text-sm hover:bg-[#FFD400] hover:text-[#151515] transition-colors">
                  Rezervo Taxi <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ OVERVIEW + BOOKING ============ */}
      <section id="booking" className="bg-white pt-20 md:pt-28 pb-16 md:pb-24">
        <div className="max-w-[1180px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          {/* LEFT overview */}
          <div className="lg:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#6D6D69] mb-4">Rreth Itinerarit</div>
            <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight text-[#151515] leading-[1.1]">
              Udhëtim i drejtpërdrejtë nga <span className="font-serif italic font-normal">{route.origin}</span> drejt {route.destination}it
            </h2>
            <div className="mt-6 space-y-4 text-[#151515]/80 text-base leading-relaxed">
              {(route.overview || []).map((p, i) => (<p key={i}>{p}</p>))}
              {(!route.overview || route.overview.length === 0) && (
                <>
                  <p>Ky shërbim është i përshtatshëm për familje, çifte dhe grupe të vogla që kërkojnë transport të drejtpërdrejtë nga {route.origin} drejt {route.destination}it, pa ndalesa të panevojshme.</p>
                  <p>Marrja mund të organizohet nga një adresë e caktuar në {route.origin}, ndërsa mbërritja bëhet deri në adresën e konfirmuar në {route.destination}.</p>
                </>
              )}
            </div>

            <ol className="mt-10 divide-y divide-[#E3E1DA] border-y border-[#E3E1DA]">
              {[
                { n: "01", t: "Marrje nga adresa", d: "Shoferi ju merr në pikën e konfirmuar gjatë rezervimit." },
                { n: "02", t: "Automjet privat", d: "Udhëtimi organizohet vetëm për rezervimin tuaj dhe nuk ndahet me pasagjerë të tjerë." },
                { n: "03", t: "Mbërritje deri në destinacion", d: "Transport deri në hotel, apartament, plazh ose adresën e përcaktuar." },
              ].map((r) => (
                <li key={r.n} className="grid grid-cols-12 gap-4 py-6 items-start">
                  <div className="col-span-2 md:col-span-1 font-display text-sm font-semibold text-[#6D6D69] pt-0.5">{r.n}</div>
                  <div className="col-span-10 md:col-span-11">
                    <h3 className="font-display text-base md:text-lg font-semibold text-[#151515]">{r.t}</h3>
                    <p className="mt-1.5 text-[#6D6D69] text-sm leading-relaxed">{r.d}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {/* RIGHT sticky form */}
          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              <div className="bg-[#F6F6F3] rounded-3xl border border-[#E3E1DA] p-6 md:p-8">
                <h2 className="font-display text-xl md:text-2xl font-semibold text-[#151515] tracking-tight">Rezervo Taxi</h2>
                <p className="text-[#6D6D69] text-sm mt-2 leading-relaxed">
                  Plotësoni të dhënat e udhëtimit dhe ne do t&rsquo;ju konfirmojmë disponueshmërinë dhe çmimin.
                </p>

                {status === "success" ? (
                  <div className="mt-6 bg-white border border-[#FFD400] rounded-2xl p-6 text-center">
                    <CheckCircle2 className="h-8 w-8 text-[#294F3B] mx-auto mb-3" />
                    <p className="font-display font-semibold text-[#151515]">Kërkesa u dërgua!</p>
                    <p className="text-[#6D6D69] text-sm mt-1">Do t&rsquo;ju kontaktojmë brenda pak minutash.</p>
                    <button
                      type="button"
                      onClick={() => setStatus("idle")}
                      className="mt-4 text-sm font-semibold text-[#151515] underline"
                    >
                      Rezervo përsëri
                    </button>
                  </div>
                ) : (
                  <form onSubmit={onSubmit} className="mt-6 space-y-3">
                    <div>
                      <label className="block text-[10px] uppercase tracking-[0.15em] font-semibold text-[#6D6D69] mb-1.5">
                        Adresa e marrjes *
                      </label>
                      <PlaceAutocompleteInput
                        id="route-pickup"
                        data-testid="route-booking-pickup"
                        required
                        value={form.pickup.address}
                        onChange={(pickup) => setForm((f) => ({ ...f, pickup }))}
                        placeholder={`P.sh. hotel / adresë në ${route.origin}`}
                        className="w-full bg-white border border-[#E3E1DA] rounded-xl px-3.5 py-3 text-sm text-[#151515] focus:outline-none focus:border-[#151515]"
                      />
                      <p className="text-[11px] text-[#6D6D69] mt-1">Kërkoni adresë ose emër biznesi</p>
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-[0.15em] font-semibold text-[#6D6D69] mb-1.5">
                        Destinacioni *
                      </label>
                      <PlaceAutocompleteInput
                        id="route-dropoff"
                        data-testid="route-booking-dropoff"
                        required
                        value={form.dropoff.address}
                        onChange={(dropoff) => setForm((f) => ({ ...f, dropoff }))}
                        placeholder={`P.sh. hotel / adresë në ${route.destination}`}
                        className="w-full bg-white border border-[#E3E1DA] rounded-xl px-3.5 py-3 text-sm text-[#151515] focus:outline-none focus:border-[#151515]"
                      />
                      <p className="text-[11px] text-[#6D6D69] mt-1">Kërkoni adresë ose emër biznesi</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Field type="date" label="Data *" name="date" value={form.date} onChange={onChange} required />
                      <Field type="time" label="Ora *" name="time" value={form.time} onChange={onChange} required />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Field type="number" label="Pasagjerë" name="passengers" value={form.passengers} onChange={onChange} min="1" max="8" required />
                      <Field type="number" label="Bagazhe" name="luggage" value={form.luggage} onChange={onChange} min="0" max="10" />
                    </div>
                    <Field label="Emri dhe mbiemri *" name="name" value={form.name} onChange={onChange} required />
                    <div>
                      <label className="block text-[10px] uppercase tracking-[0.15em] font-semibold text-[#6D6D69] mb-1.5">
                        Telefoni *
                      </label>
                      <PhoneInput
                        data-testid="route-booking-phone"
                        defaultCountry="AL"
                        international
                        countryCallingCodeEditable={false}
                        value={form.phone}
                        onChange={(value) => setForm((f) => ({ ...f, phone: value || "" }))}
                        placeholder="+355 6X XXX XXXX"
                        className="PhoneInput route-phone-input"
                      />
                    </div>
                    <Field type="email" label="Email (opsionale)" name="email" value={form.email} onChange={onChange} />
                    <div>
                      <label className="block text-[10px] uppercase tracking-[0.15em] font-semibold text-[#6D6D69] mb-1.5">Mesazh opsional</label>
                      <textarea name="notes" rows={3} value={form.notes} onChange={onChange} className="w-full bg-white border border-[#E3E1DA] rounded-xl px-3.5 py-3 text-sm text-[#151515] focus:outline-none focus:border-[#151515] resize-none" />
                    </div>
                    {status === "error" && errorMsg && (
                      <div className="flex items-start gap-2 text-red-600 text-sm font-semibold">
                        <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                        <span>{errorMsg}</span>
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      className="w-full mt-2 inline-flex items-center justify-center gap-2 bg-[#151515] text-white font-semibold rounded-full px-5 py-3.5 text-sm hover:bg-[#FFD400] hover:text-[#151515] transition-colors disabled:opacity-60"
                    >
                      {status === "submitting" ? (<><Loader2 className="h-4 w-4 animate-spin" /> Po dërgohet...</>) : (<>Rezervo Taxi <ArrowRight className="h-4 w-4" /></>)}
                    </button>
                    <p className="text-[11px] text-[#6D6D69] leading-relaxed mt-3">
                      Automjeti caktohet sipas numrit të pasagjerëve, bagazheve dhe disponueshmërisë.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* ============ PICKUP + ARRIVAL AREAS ============ */}
      <section className="bg-[#FAF6E8] py-20 md:py-24">
        <div className="max-w-[1180px] mx-auto px-6 md:px-10">
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#6D6D69] mb-4">Pikat e udhëtimit</div>
          <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight text-[#151515] leading-[1.1] max-w-xl">
            Nga adresa juaj deri në destinacion
          </h2>
          <p className="mt-4 text-[#6D6D69] text-base leading-relaxed max-w-xl">
            Marrja dhe mbërritja mund të organizohen sipas adresës së përcaktuar gjatë rezervimit.
          </p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            <AreaCard
              title={`Marrja në ${route.origin}`}
              items={route.pickup_areas || ["Qendra e Lushnjës", "Stacioni i autobusëve", "Hotel ose banesë private", "Zona përreth sipas marrëveshjes"]}
            />
            <AreaCard
              title={`Mbërritja në ${route.destination}`}
              items={route.arrival_areas || [`${route.destination} fshat / qytet`, "Hotel apo apartament", "Plazhi ose zona pushimi", "Adresa private"]}
            />
          </div>
        </div>
      </section>

      {/* ============ FLEET INFO ============ */}
      <section className="bg-white py-20 md:py-24">
        <div className="max-w-[1180px] mx-auto px-6 md:px-10">
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#6D6D69] mb-4">Flota</div>
          <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight text-[#151515] leading-[1.1] max-w-2xl">
            Flota që shërben këtë itinerar
          </h2>
          <p className="mt-4 text-[#6D6D69] text-base leading-relaxed max-w-2xl">
            Automjeti caktohet nga Brothers Taxi sipas numrit të pasagjerëve, sasisë së bagazheve, tipit të udhëtimit dhe disponueshmërisë.
          </p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            {fleet.map((v, i) => (
              <article key={i} className="rounded-3xl border border-[#E3E1DA] overflow-hidden bg-white">
                <div className="aspect-[4/3] overflow-hidden bg-[#F6F6F3]">
                  <img src={v.img} alt={v.name} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="p-6">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#294F3B]">{v.cat}</div>
                  <h3 className="font-display text-lg md:text-xl font-semibold text-[#151515] mt-2 tracking-tight">{v.name}</h3>
                  <p className="mt-3 text-[#6D6D69] text-sm leading-relaxed">{v.desc}</p>
                  <ul className="mt-4 space-y-1.5 text-sm text-[#151515]">
                    {v.caps.map((c, ci) => (<li key={ci} className="flex items-center gap-2"><span className="h-1 w-1 rounded-full bg-[#151515]" />{c}</li>))}
                  </ul>
                </div>
              </article>
            ))}
          </div>

          <div className="mt-8 flex flex-col md:flex-row md:items-center justify-between gap-4 pt-6 border-t border-[#E3E1DA]">
            <p className="text-[#6D6D69] text-sm max-w-2xl leading-relaxed">
              Modeli konkret mund të ndryshojë sipas disponueshmërisë, ndërsa kategoria dhe kapaciteti konfirmohen përpara udhëtimit.
            </p>
            <Link to="/fleet" className="inline-flex items-center gap-1.5 text-[#151515] font-semibold text-sm underline decoration-[#FFD400] decoration-2 underline-offset-4 hover:decoration-[#151515]">
              Shiko flotën tonë <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ DESTINATION INFO ============ */}
      <section className="bg-[#F6F6F3] py-20 md:py-24">
        <div className="max-w-[1180px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="rounded-3xl overflow-hidden border border-[#E3E1DA]">
              <img
                src={route.destination_image || route.hero_image || CTA_IMG}
                alt={`${route.destination}, destinacion nga Lushnja me Brothers Taxi`}
                className="w-full aspect-[4/3] object-cover"
                loading="lazy"
              />
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#6D6D69] mb-4">Rreth destinacionit</div>
            <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight text-[#151515] leading-[1.1]">
              Mbërritja në {route.destination}
            </h2>
            <div className="mt-6 space-y-4 text-[#151515]/80 text-base leading-relaxed">
              {(route.destination_content || [
                `Brothers Taxi ju dërgon deri në adresën e konfirmuar në ${route.destination}. Rruga është organizuar për t&rsquo;ju çuar drejtpërdrejt në hotel, apartament ose adresën private, pa ndalesa të panevojshme.`,
                "Gjatë sezonit të lartë, planifikoni orarin e nisjes sipas trafikut të parashikuar. Për bagazhe të mëdha ose kërkesa të veçanta, na njoftoni në kohën e rezervimit."
              ]).map((p, i) => (<p key={i}>{p}</p>))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ WHAT'S INCLUDED ============ */}
      <section className="bg-white py-20 md:py-24">
        <div className="max-w-[1180px] mx-auto px-6 md:px-10">
          <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight text-[#151515] leading-[1.1] max-w-xl">
            Çfarë përfshin udhëtimi
          </h2>
          <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-0 divide-y md:divide-y-0 divide-[#E3E1DA]">
            {includedItems.map((item, i) => (
              <li key={i} className="flex items-center gap-4 py-5 md:border-b md:border-[#E3E1DA]">
                <span className="font-display text-xs font-semibold text-[#6D6D69] w-6">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-[#151515] text-base">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="bg-[#151515] text-white py-20 md:py-24">
        <div className="max-w-[1180px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#FFD400] mb-4">Pyetje të shpeshta</div>
            <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight leading-[1.1]">
              Para se të <span className="font-serif italic font-normal">niseni</span>
            </h2>
          </div>
          <div className="lg:col-span-8">
            <div className="divide-y divide-white/10 border-y border-white/10">
              {(route.faqs || defaultFaqs(route)).map((f, i) => (
                <details key={i} className="group py-5">
                  <summary className="flex items-start justify-between gap-6 cursor-pointer list-none">
                    <span className="font-display text-base md:text-lg font-medium">{f.q}</span>
                    <ChevronDown className="h-5 w-5 flex-shrink-0 mt-1 group-open:rotate-180 transition-transform" />
                  </summary>
                  <p className="mt-4 text-white/70 text-sm md:text-base leading-relaxed max-w-2xl">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ RELATED ROUTES ============ */}
      <section className="bg-white py-20 md:py-24">
        <div className="max-w-[1180px] mx-auto px-6 md:px-10">
          <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight text-[#151515] leading-[1.1]">
            Destinacione të ngjashme
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/${r.slug}`}
                className="group flex items-center justify-between gap-3 bg-[#F6F6F3] hover:bg-[#FFD400] border border-[#E3E1DA] rounded-2xl px-5 py-5 transition-colors"
              >
                <div>
                  <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#6D6D69] group-hover:text-[#151515]">Brothers Taxi</div>
                  <div className="font-display text-base md:text-lg font-semibold text-[#151515] mt-1">
                    Taxi {r.origin} – {r.destination}
                  </div>
                  {r.price_all && <div className="text-[#6D6D69] text-sm mt-0.5 group-hover:text-[#151515]/70">Nga {formatLek(r.price_all)}</div>}
                </div>
                <ArrowUpRight className="h-5 w-5 text-[#151515] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <img src={CTA_IMG} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#151515]/70" />
        <div className="relative max-w-[1180px] mx-auto px-6 md:px-10 text-center">
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-white leading-[1.1] max-w-3xl mx-auto">
            Rezervo Taxi nga <span className="font-serif italic font-normal">{route.origin}</span> në {route.destination}
          </h2>
          <p className="mt-5 text-white/80 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            Na dërgoni datën, orën dhe pikën e marrjes. Ekipi i Brothers Taxi do t&rsquo;ju konfirmojë çmimin, automjetin dhe disponueshmërinë.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <a href="#booking" className="inline-flex items-center gap-2 bg-[#FFD400] text-[#151515] font-semibold rounded-full px-7 py-3.5 text-sm hover:bg-white transition-colors">
              Rezervo Taxi <ArrowRight className="h-4 w-4" />
            </a>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-transparent border border-white/40 text-white font-semibold rounded-full px-7 py-3.5 text-sm hover:bg-white hover:text-[#151515] hover:border-white transition-colors">
              Na kontaktoni
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

const RouteMeta = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#6D6D69] mb-1">{label}</div>
    <div className="font-display text-base md:text-lg font-semibold text-[#151515] tracking-tight">{value}</div>
  </div>
);

const AreaCard = ({ title, items }) => (
  <div className="bg-white rounded-3xl border border-[#E3E1DA] p-7 md:p-8">
    <h3 className="font-display text-lg md:text-xl font-semibold text-[#151515] tracking-tight">{title}</h3>
    <ul className="mt-5 space-y-3">
      {items.map((it, i) => (
        <li key={i} className="flex items-center gap-3 text-[#151515]/85">
          <span className="h-1 w-1 rounded-full bg-[#FFD400]" />
          {it}
        </li>
      ))}
    </ul>
  </div>
);

const Field = ({ label, name, ...props }) => (
  <div>
    <label htmlFor={name} className="block text-[10px] uppercase tracking-[0.15em] font-semibold text-[#6D6D69] mb-1.5">{label}</label>
    <input
      id={name}
      name={name}
      {...props}
      className="w-full bg-white border border-[#E3E1DA] rounded-xl px-3.5 py-3 text-sm text-[#151515] focus:outline-none focus:border-[#151515]"
    />
  </div>
);

const defaultFaqs = (r) => [
  { q: `Sa zgjat udhëtimi nga ${r.origin} në ${r.destination}?`, a: `Kohëzgjatja mesatare është ${r.duration_text?.toLowerCase() || "sipas trafikut"}, në varësi të kushteve të trafikut dhe sezonit.` },
  { q: `Sa kushton taxi nga ${r.origin} drejt ${r.destination}?`, a: `Çmimi bazë për një automjet privat, një drejtim, është ${formatLek(r.price_all)}. Çmimi konfirmohet përpara nisjes.` },
  { q: "A është çmimi për person apo për automjet?", a: "Çmimi është për automjet, jo për person. Deri në 4 pasagjerë udhëtojnë me të njëjtin çmim." },
  { q: "A mund të rezervoj edhe udhëtimin e kthimit?", a: "Po. Mund të organizohet një udhëtim kthimi me datë dhe orë të përcaktuar." },
  { q: "A mund të kërkoj një ndalesë gjatë rrugës?", a: "Po, ndalesat e shkurtra janë të mundshme. Për ndalesa më të gjata mund të aplikohet tarifë shtesë pritjeje." },
  { q: "Sa bagazhe mund të marrim?", a: "Në varësi të automjetit, mund të transportohen 3–4 valixhe standarde. Për bagazhe të veçanta, na njoftoni paraprakisht." },
];

export default RouteDetail;
